/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { Fragment, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_EPICS_SUGGEST_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type OrganisedIssues } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { IssueSelector } from '../../common/ui/issue-selector';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { useBulkAssignIssuesToEpic } from '../../services';
import { findIssueAri } from '../../services/utils';

import messages from './messages';

const styles = cssMap({
	errorMessage: {
		paddingInlineStart: token('space.025'),
	},

	epicIssueSelector: {
		paddingLeft: token('space.200'),
		marginTop: token('space.100'),
	},

	actionsWrapper: {
		marginTop: token('space.100'),
	},

	assignButton: {
		width: 'fit-content',
	},
});

export const filterJiraEpicSuggestActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraEpicsSuggest]>[] => {
	const filteredActions = actions.filter(assertIsJiraEpicsSuggestAction);
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraEpicsSuggest]
	>[];
};

const assertIsJiraEpicsSuggestAction = (action: MessageAction): MessageAction => {
	// TODO: assert action.key === JIRA_EPICS_SUGGEST_KEY
	// or other minimal sanity
	if (action.key !== JIRA_EPICS_SUGGEST_KEY) {
		throw new Error('Invalid action key for JiraEpicsSuggestAction');
	}
	return action;
};

const JIRA_EPICS_SUGGEST_INTERACTION_SOURCE = 'jiraEpicsSuggestRenderer';

export const JiraEpicsSuggestAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraEpicsSuggest>,
): JSX.Element => {
	return <JiraEpicsSuggestActionNewLayout {...props} />;
};

export const JiraEpicsSuggestActionNewLayout = ({
	actions,
	getStatus,
	getReadonly,
	recipientAgentId,
	recipientAgentNamedId,
	conversationId,
	messageId,
	onCustomDataActioned,
	onActioned,
	sendMessageConfirmedTools,
	cloudId,
}: ActionRendererFor<typeof ActionKeys.JiraEpicsSuggest>): JSX.Element => {
	di(IssueSelector);
	di(useBulkAssignIssuesToEpic);

	const { formatMessage } = useIntl();

	const firstAction = actions[0];
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const epics = firstAction.data.epics;
	const isUnableToMatchEpics = epics.length === 0;

	// TODO: use just issueAris instead across controller to avoid conversions, but first we need to make sure IssueSelector adopts that
	//   which requires CustomActinos adopt that
	const [selectedIssueKeysByEpic, setSelectedIssueKeysByEpic] = useState<OrganisedIssues>({});
	const epicWithSelectedIssuesCount = Object.values(selectedIssueKeysByEpic).filter(
		(issues) => issues.length > 0,
	).length;
	const hasSelectedAtLeastOneIssue = epicWithSelectedIssuesCount > 0;

	const { loading, initiateBulkOperation } = useBulkAssignIssuesToEpic();

	const [shouldShowAssignErrorMessage, setShouldShowAssignErrorMessage] = useState(false);

	const { sendAnalyticsEvent } = useAnalytics();

	const handleIssuesSelected = useCallback(
		(epicKey: string, issueKeys: string[]) => {
			setSelectedIssueKeysByEpic((prevState) => {
				const newState = {
					...prevState,
					[epicKey]: issueKeys,
				};
				if (shouldShowAssignErrorMessage && hasSelectedAtLeastOneIssue) {
					setShouldShowAssignErrorMessage(false);
				}
				return newState;
			});
		},
		[shouldShowAssignErrorMessage, hasSelectedAtLeastOneIssue],
	);

	if (isUnableToMatchEpics) {
		return <Text>{firstAction.data.resultSummary}</Text>;
	}

	const handleBulkOperation = async () => {
		if (!hasSelectedAtLeastOneIssue) {
			setShouldShowAssignErrorMessage(true);
			return;
		}

		const selectedIssueArisByEpic = Object.entries(selectedIssueKeysByEpic).reduce(
			(acc, [epicKey, issueAris]) => {
				const issues = epics.find((epic) => epic.issueKey === epicKey)?.issues || [];
				const issueKeys = issueAris.map((issueAri) => findIssueAri(issues, issueAri));
				if (issueKeys.length > 0) {
					acc[epicKey] = issueKeys;
				}
				return acc;
			},
			{} as Record<string, string[]>,
		);

		const selectedEpics = Object.keys(selectedIssueArisByEpic);
		const updatedData = {
			...firstAction.data,
			customData: {
				organisedIssueKeys: selectedIssueKeysByEpic,
			},
		};

		const sendClickAnalytics = () => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'epicSuggestActionButton',
				attributes: {
					recipientAgentNamedId,
					actionKey: JIRA_EPICS_SUGGEST_KEY,
					selectedEpicCount: selectedEpics.length,
					totalEpicCount: epics.length,
					selectedIssueCount: selectedIssueKeysByEpic.length,
					totalIssueCount:
						firstAction.data.providedIssues.length - firstAction.data.unorganisedIssues.length,
					unOrganisedIssueCount: firstAction.data.unorganisedIssues.length,
				},
			});
		};

		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			sendClickAnalytics();
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: JIRA_EPICS_SUGGEST_INTERACTION_SOURCE,
				},
				confirmed_tools: {
					message_id: messageId,
					tools: [buildToolFromAction(firstAction, 'confirmed', updatedData)],
				},
			});
			return;
		}

		try {
			await initiateBulkOperation(selectedIssueArisByEpic, cloudId);
			sendClickAnalytics();

			onCustomDataActioned(
				firstAction,
				updatedData,
				formatMessage(messages.jiraAssignIssueSuccessMessageText, {
					epicLinks: epics
						.filter((epic) => selectedEpics.includes(epic.issueKey))
						.map((epic, index) => `${index + 1}. ${epic.url}`)
						.join('\n'),
					totalEpics: selectedEpics.length,
				}),
			);
		} catch (e) {
			if (e instanceof Error) {
				onActioned(firstAction, e.message);
			} else {
				onActioned(
					firstAction,
					formatMessage(messages.jiraBulkAssignIssueUnexpectedErrorMessageText),
				);
			}
		}
	};

	return (
		<Stack space="space.250">
			<Text>{firstAction.data.resultSummary}</Text>
			<ActionCardWrapper
				title={formatMessage(messages.jiraEpicSuggestActionCardTitle)}
				product="jira"
			>
				<Stack space="space.150">
					<Text>
						{formatMessage(messages.jiraEpicSuggestSubtitleText, {
							epicsCount: firstAction.data.epics.length,
						})}
					</Text>
					<Box as="ol" xcss={styles.epicIssueSelector}>
						{epics.map((epic) => (
							<Fragment key={epic.issueKey}>
								<li>
									<Card url={epic.url} appearance="inline" platform="web" showHoverPreview={true} />
									<Box paddingBlock="space.150">
										<IssueSelector
											initialIssues={epic.issues}
											title={formatMessage(messages.jiraIssuesDropdownTitleText)}
											isInitiallyExpanded={true}
											epicKey={epic.issueKey}
											selectedIssuesByEpic={selectedIssueKeysByEpic}
											onIssuesSelected={(issueKeys) => {
												handleIssuesSelected(epic.issueKey, issueKeys);
											}}
											initialSelectedIssues={
												firstAction.data.customData?.organisedIssueKeys[epic.issueKey]
											}
											readonly={readonly}
										/>
									</Box>
								</li>
							</Fragment>
						))}
					</Box>
				</Stack>

				<ActionCardFooter>
					<Button
						appearance="primary"
						isDisabled={readonly || status !== 'idle'}
						isLoading={loading}
						onClick={handleBulkOperation}
					>
						{formatMessage(messages.jiraEpicAssignButton, {
							epicCount: epicWithSelectedIssuesCount,
						})}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>

			<div role="region" aria-live="polite">
				{shouldShowAssignErrorMessage && (
					<Inline alignBlock="center" space="space.075" xcss={styles.errorMessage}>
						<WarningIcon label="" color={token('color.icon.danger')} size="small" />
						<Text color="color.text.danger">
							{formatMessage(messages.jiraAssignIssueErrorMessageText)}
						</Text>
					</Inline>
				)}
			</div>
		</Stack>
	);
};
